import { useEffect } from 'react';
import { ClientError, APIError, useQueryClient, useErrorHandler, useLoaderToast, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { copyGroup } from 'api/groups/groups';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useSelectedGroup } from 'hooks/useSelectedGroup/useSelectedGroup';
import { useModalState } from 'hooks/useModalState/useModalState';
import { isCreateGroupJobResponse } from 'app/groups/utils/groupUpdateHelpers';

import { CopyGroup } from './CopyGroup';
import { CopyGroupContainerProps, CopyGroupFormBody } from './CopyGroup.types';
import { useCopyGroupValidation } from './CopyGroup.validation';

export const CopyGroupContainer = ({ selectedGroup }: CopyGroupContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { setSelectedGroup } = useSelectedGroup();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { schema } = useCopyGroupValidation();
  const { handleError } = useErrorHandler();

  const { load: loadCopyGroup, update: updateLoadCopyGroup, close: closeLoadCopyGroup } = useLoaderToast('copyGroup');

  const handleJobComplete = () => {
    updateLoadCopyGroup(formatMessage({ id: AppMessages['group.copy.success'] }));
    setSelectedGroup(undefined);
    queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
  };

  const handleJobError = () => {
    toast.error(formatMessage({ id: AppMessages['group.copy.error'] }));
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: false,
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['group.copy.title'] }),
  });

  const { mutate: onCopyGroup, isLoading } = useMutation(copyGroup, {
    onMutate: () => loadCopyGroup(formatMessage({ id: AppMessages['group.copy.loading'] })),
    onSuccess: (response) => {
      if (isCreateGroupJobResponse(response)) {
        jobStatus.startJob(response.jobId);
        onModalClose();
      } else {
        setSelectedGroup(undefined);
        queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
        updateLoadCopyGroup(formatMessage({ id: AppMessages['group.copy.success'] }));
        onModalClose();
      }
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;

      const isIdExistsError = errorFields?.find(({ type, field }) => type === 'exists' && field === 'idGrupy');

      if (isIdExistsError) {
        closeLoadCopyGroup();
        return methods.setError('idGrupy', { message: formatMessage({ id: AppMessages['group.copy.error.exists'] }) });
      }

      handleError({ error, omitAPIErrorMessage: true, updateToastCallback: updateLoadCopyGroup });
    },
  });

  const methods = useForm<CopyGroupFormBody>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: { idGrupy: '', nazwa: '' },
  });

  useEffect(() => {
    methods.reset({ idGrupy: '', nazwa: '' });
  }, [isModalOpen, selectedGroup]);

  const onSubmit = (formBody: CopyGroupFormBody) => {
    onCopyGroup({ body: { oldId: selectedGroup.id, ...formBody } });
  };

  return (
    <FormProvider {...methods}>
      <CopyGroup
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
